import React, { useState } from 'react';
import ActivateConfirmModal from './ActivateConfirmModal';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import { Checkbox } from '../../../components/ui/Checkbox';
import Icon from '../../../components/AppIcon';

const ClientTable = ({ 
  clients, 
  onEdit, 
  onDelete, 
  onBulkDelete, 
  onExport,
  searchTerm,
  onSearchChange,
  roleFilter,
  onRoleFilterChange,
  statusFilter,
  onStatusFilterChange,
  sortConfig,
  onSort,
  onActivate, // callback for table refresh
}) => {
  // Activate modal state
  const [activateModalOpen, setActivateModalOpen] = useState(false);
  const [activateTarget, setActivateTarget] = useState(null);
  const [activateLoading, setActivateLoading] = useState(false);
  const [activateSuccess, setActivateSuccess] = useState(null);
  // Activate handler
  const handleActivateClick = (client) => {
    setActivateTarget(client);
    setActivateModalOpen(true);
  };

  const handleActivateConfirm = async () => {
    if (!activateTarget) return;
    setActivateLoading(true);
    try {
      const token = localStorage.getItem('authToken');
      const res = await fetch(`${import.meta.env.VITE_AUTH_BASE_URL}/client/${activateTarget.id}/status`, {
        method: 'PUT',
        headers: {
          accept: '*/*',
          Authorization: `Bearer ${token || ''}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ status_id: 1 }),
      });
      const result = await res.json();
      if (!res.ok || result?.status !== 'success') {
        throw new Error(result?.message || `Failed with status ${res.status}`);
      }
      setActivateSuccess('Item activated successfully');
      setActivateModalOpen(false);
      setActivateTarget(null);
      if (onActivate) onActivate();
    } catch (err) {
      setActivateSuccess('Activation failed');
    } finally {
      setActivateLoading(false);
      setTimeout(() => setActivateSuccess(null), 2500);
    }
  };
  const [selectedClients, setSelectedClients] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  const roleOptions = [
    { value: '', label: 'All Roles' },
    { value: '1', label: 'Admin' },
    { value: '4', label: 'Courier' },
    { value: '3', label: 'Client' }
  ];

  const statusOptions = [
    { value: '', label: 'All Status' },
    { value: 'active', label: 'Active' },
    { value: 'blocked', label: 'Inactive' },
    { value: 'in_process', label: 'Inprocess' }
  ];

  const handleSelectAll = (checked) => {
    if (checked) {
      setSelectedClients(clients?.map(client => client?.id));
    } else {
      setSelectedClients([]);
    }
  };

  const handleSelectClient = (clientId, checked) => {
    if (checked) {
      setSelectedClients(prev => [...prev, clientId]);
    } else {
      setSelectedClients(prev => prev?.filter(id => id !== clientId));
    }
  };

  const handleBulkDelete = () => {
    if (selectedClients?.length > 0) {
      onBulkDelete(selectedClients);
      setSelectedClients([]);
    }
  };

  const getSortIcon = (column) => {
    if (sortConfig?.key !== column) return 'ArrowUpDown';
    return sortConfig?.direction === 'asc' ? 'ArrowUp' : 'ArrowDown';
  };

  const formatDate = (dateString) => {
    return new Date(dateString)?.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      active: { color: 'text-success bg-success/10 border-success/20', label: 'Active' },
      blocked: { color: 'text-muted-foreground bg-muted border-border', label: 'Inactive' },
      in_process: { color: 'text-error bg-error/10 border-error/20', label: 'Inprocess' }
    };

    const config = statusConfig?.[status] || statusConfig?.inactive;
    return (
      <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium border ${config?.color}`}>
        {config?.label}
      </span>
    );
  };

  const getRoleBadge = (role) => {
    const roleConfig = {
      admin: { color: 'text-primary bg-primary/10 border-primary/20', label: 'Admin' },
      client: { color: 'text-accent bg-accent/10 border-accent/20', label: 'Client' },
      viewer: { color: 'text-muted-foreground bg-muted border-border', label: 'Client' }
    };

    const config = roleConfig?.[role] || roleConfig?.viewer;
    return (
      <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium border ${config?.color}`}>
        {config?.label}
      </span>
    );
  };

  // Filter and sort clients
  const filteredClients = clients?.filter(client => {
    const matchesSearch = client?.name?.toLowerCase()?.includes(searchTerm?.toLowerCase()) ||
                         client?.email?.toLowerCase()?.includes(searchTerm?.toLowerCase());
    const matchesRole = !roleFilter || client?.role === roleFilter;
    const matchesStatus = !statusFilter || client?.status === statusFilter;
    
    return matchesSearch && matchesRole && matchesStatus;
  });

  const sortedClients = [...filteredClients]?.sort((a, b) => {
    if (!sortConfig?.key) return 0;
    
    const aValue = a?.[sortConfig?.key];
    const bValue = b?.[sortConfig?.key];
    
    if (sortConfig?.direction === 'asc') {
      return aValue > bValue ? 1 : -1;
    } else {
      return aValue < bValue ? 1 : -1;
    }
  });

  // Pagination
  const totalPages = Math.ceil(sortedClients?.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedClients = sortedClients?.slice(startIndex, startIndex + itemsPerPage);

  const isAllSelected = selectedClients?.length === paginatedClients?.length && paginatedClients?.length > 0;
  const isIndeterminate = selectedClients?.length > 0 && selectedClients?.length < paginatedClients?.length;

  return (
    <div className="space-y-6">
      {/* Filters and Actions */}
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
        <div className="flex flex-col sm:flex-row gap-4 flex-1">
          <div className="flex-1 max-w-md">
            <Input
              type="search"
              placeholder="Search clients by name or email..."
              value={searchTerm}
              onChange={(e) => onSearchChange(e?.target?.value)}
              className="w-full"
            />
          </div>
          
          <div className="flex gap-3">
            <Select
              options={roleOptions}
              value={roleFilter}
              onChange={onRoleFilterChange}
              placeholder="Filter by role"
              className="w-40"
            />
            
            <Select
              options={statusOptions}
              value={statusFilter}
              onChange={onStatusFilterChange}
              placeholder="Filter by status"
              className="w-40"
            />
          </div>
        </div>

        <div className="flex items-center gap-3">
          {selectedClients?.length > 0 && (
            <Button
              variant="destructive"
              size="sm"
              iconName="Trash2"
              onClick={handleBulkDelete}
            >
              Delete Selected ({selectedClients?.length})
            </Button>
          )}
          
          <Button
            variant="outline"
            size="sm"
            iconName="Download"
            onClick={onExport}
          >
            Export Excel
          </Button>
        </div>
      </div>
      {/* Desktop Table */}
      <div className="hidden lg:block bg-card border border-border rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-muted/50 border-b border-border">
              <tr>
                <th className="w-12 px-4 py-3">
                  <Checkbox
                    checked={isAllSelected}
                    indeterminate={isIndeterminate}
                    onChange={(e) => handleSelectAll(e?.target?.checked)}
                  />
                </th>
                <th className="px-4 py-3 text-left text-sm font-medium text-foreground">
                  <button
                    onClick={() => onSort('id')}
                    className="flex items-center space-x-2 hover:text-primary transition-colors"
                  >
                    <span>S.No</span>
                    <Icon name={getSortIcon('id')} size={14} />
                  </button>
                </th>
                <th className="px-4 py-3 text-left text-sm font-medium text-foreground">
                  <button
                    onClick={() => onSort('name')}
                    className="flex items-center space-x-2 hover:text-primary transition-colors"
                  >
                    <span>Name</span>
                    <Icon name={getSortIcon('name')} size={14} />
                  </button>
                </th>
                <th className="px-4 py-3 text-left text-sm font-medium text-foreground">
                  <button
                    onClick={() => onSort('email')}
                    className="flex items-center space-x-2 hover:text-primary transition-colors"
                  >
                    <span>Client Code</span>
                    <Icon name={getSortIcon('email')} size={14} />
                  </button>
                </th>
                {/* <th className="px-4 py-3 text-left text-sm font-medium text-foreground">
                  <button
                    onClick={() => onSort('createdDate')}
                    className="flex items-center space-x-2 hover:text-primary transition-colors"
                  >
                    <span>Created Date</span>
                    <Icon name={getSortIcon('createdDate')} size={14} />
                  </button>
                </th> */}
                <th className="px-4 py-3 text-left text-sm font-medium text-foreground">Role</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-foreground">Status</th>
                <th className="px-4 py-3 text-center text-sm font-medium text-foreground">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {paginatedClients?.map((client, index) => (
                <tr key={client?.id} className="hover:bg-muted/30 transition-colors">
                  <td className="px-4 py-3">
                    <Checkbox
                      checked={selectedClients?.includes(client?.id)}
                      onChange={(e) => handleSelectClient(client?.id, e?.target?.checked)}
                    />
                  </td>
                  <td className="px-4 py-3 text-sm text-foreground font-medium">
                    {startIndex + index + 1}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center space-x-3">
                      <div className="w-8 h-8 bg-primary/10 rounded-full flex items-center justify-center">
                        <span className="text-sm font-medium text-primary">
                          {client?.name?.charAt(0)?.toUpperCase()}
                        </span>
                      </div>
                      <div>
                        <p className="text-sm font-medium text-foreground">{client?.name}</p>
                        <p className="text-xs text-muted-foreground">{client?.clientName}</p>
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3 text-sm text-foreground">{client?.email}</td>
                  {/* <td className="px-4 py-3 text-sm text-muted-foreground">
                    {formatDate(client?.createdDate)}
                  </td> */}
                  <td className="px-4 py-3">
                    {getRoleBadge(client?.role)}
                  </td>
                  <td className="px-4 py-3">
                    {getStatusBadge(client?.status)}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-center space-x-2">
                      <Button
                        variant="ghost"
                        size="sm"
                        iconName="Edit"
                        onClick={() => onEdit(client)}
                        className="h-8 w-8 p-0"
                      />
                      <Button
                        variant="ghost"
                        size="sm"
                        iconName="Play"
                        onClick={() => handleActivateClick(client)}
                        className="h-8 w-8 p-0 text-success hover:text-success hover:bg-success/10"
                        title="Activate"
                      />
                      <Button
                        variant="ghost"
                        size="sm"
                        iconName="Trash2"
                        onClick={() => onDelete(client?.id)}
                        className="h-8 w-8 p-0 text-error hover:text-error hover:bg-error/10"
                      />
                    </div>
                  </td>
      {/* Activate Modal */}
      <ActivateConfirmModal
        isOpen={activateModalOpen}
        onClose={() => { setActivateModalOpen(false); setActivateTarget(null); }}
        onConfirm={handleActivateConfirm}
        isLoading={activateLoading}
        clientName={activateTarget?.name || ''}
      />
      {/* Success Notification */}
      {activateSuccess && (
        <div className="fixed bottom-5 right-5 px-4 py-3 rounded shadow-lg text-white z-[99999]" style={{ minWidth: '250px' }}>
          <div className="bg-green-500 p-2 rounded">{activateSuccess}</div>
        </div>
      )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      {/* Mobile Cards */}
      <div className="lg:hidden space-y-4">
        {paginatedClients?.map((client, index) => (
          <div key={client?.id} className="bg-card border border-border rounded-lg p-4">
            <div className="flex items-start justify-between mb-3">
              <div className="flex items-center space-x-3">
                <Checkbox
                  checked={selectedClients?.includes(client?.id)}
                  onChange={(e) => handleSelectClient(client?.id, e?.target?.checked)}
                />
                <div className="w-10 h-10 bg-primary/10 rounded-full flex items-center justify-center">
                  <span className="text-sm font-medium text-primary">
                    {client?.name?.charAt(0)?.toUpperCase()}
                  </span>
                </div>
                <div>
                  <p className="font-medium text-foreground">{client?.name}</p>
                  <p className="text-sm text-muted-foreground">#{startIndex + index + 1}</p>
                </div>
              </div>
              
              <div className="flex items-center space-x-2">
                <Button
                  variant="ghost"
                  size="sm"
                  iconName="Edit"
                  onClick={() => onEdit(client)}
                  className="h-8 w-8 p-0"
                />
                <Button
                  variant="ghost"
                  size="sm"
                  iconName="Trash2"
                  onClick={() => onDelete(client?.id)}
                  className="h-8 w-8 p-0 text-error hover:text-error hover:bg-error/10"
                />
              </div>
            </div>
            
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-muted-foreground">Email:</span>
                <span className="text-foreground">{client?.email}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Client Name:</span>
                <span className="text-foreground">{client?.clientName}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Created:</span>
                <span className="text-foreground">{formatDate(client?.createdDate)}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-muted-foreground">Role:</span>
                {getRoleBadge(client?.role)}
              </div>
              <div className="flex justify-between items-center">
                <span className="text-muted-foreground">Status:</span>
                {getStatusBadge(client?.status)}
              </div>
            </div>
          </div>
        ))}
      </div>
      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between">
          <div className="text-sm text-muted-foreground">
            Showing {startIndex + 1} to {Math.min(startIndex + itemsPerPage, sortedClients?.length)} of {sortedClients?.length} clients
          </div>
          
          <div className="flex items-center space-x-2">
            <Button
              variant="outline"
              size="sm"
              iconName="ChevronLeft"
              onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
              disabled={currentPage === 1}
            >
              Previous
            </Button>
            
            <div className="flex items-center space-x-1">
              {Array.from({ length: totalPages }, (_, i) => i + 1)?.map(page => (
                <Button
                  key={page}
                  variant={currentPage === page ? "default" : "ghost"}
                  size="sm"
                  onClick={() => setCurrentPage(page)}
                  className="w-8 h-8 p-0"
                >
                  {page}
                </Button>
              ))}
            </div>
            
            <Button
              variant="outline"
              size="sm"
              iconName="ChevronRight"
              onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
              disabled={currentPage === totalPages}
            >
              Next
            </Button>
          </div>
        </div>
      )}
      {/* Empty State */}
      {filteredClients?.length === 0 && (
        <div className="text-center py-12">
          <Icon name="Users" size={48} className="mx-auto text-muted-foreground mb-4" />
          <h3 className="text-lg font-medium text-foreground mb-2">No clients found</h3>
          <p className="text-muted-foreground mb-4">
            {searchTerm || roleFilter || statusFilter 
              ? "Try adjusting your search or filter criteria" :"Get started by adding your first client"
            }
          </p>
        </div>
      )}
    </div>
  );
};

export default ClientTable;